import { ClientsPermission } from '@idc-forms/permissions'
import { FileClock, GitBranch, Handshake, History, Smartphone } from 'lucide-react'
import { VersionTimeline } from '@/features/entity-versions/components/version-timeline'
import { useCanReadEntityVersions } from '@/features/entity-versions/hooks/use-entity-versions'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { FEATURES } from '@/shared/config/features'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { useClientV2DetailQuery, useClientV2Remove } from '../../hooks/use-clients-admin-v2-query'
import type { ClientsAdminV2Row } from '../../services/clients-admin-v2.service'
import { ClientAppTab } from './client-app-tab'
import { ClientDetailActions } from './client-detail-actions'
import { ClientDetailSummary } from './client-detail-summary'
import { ClientFlowsTab } from './client-flows-tab'
import { ClientHistoryTab } from './client-history-tab'
import { ClientNegotiationsTab } from './client-negotiations-tab'

type ClientDetailV2PanelProps = {
  client: ClientsAdminV2Row
}

/**
 * Panel del cliente contra el expediente v2. Misma composición que el panel v1
 * —resumen, acciones y las pestañas Negociaciones / Flujos / Historial—; lo que
 * cambia es de dónde salen los datos.
 *
 * DOS CONSULTAS MENOS. El dossier v2 devuelve empresa y agencia ya resueltas a
 * `{ id, name }`, así que el panel no necesita bajar los dos catálogos completos
 * para traducir un id a un nombre, que es lo que hacía sobre v1.
 *
 * EL TOKEN DEL PORTAL NO VIENE DE LA FILA. El listado v2 no lo publica: es una
 * credencial, y una tabla de 436 clientes no tiene por qué bajar 436 enlaces
 * vivos al navegador para copiar uno. `ActiveFlowPanel` lo toma del flujo que
 * ya consulta —lo necesita igual para decidir qué mostrar—, que es su dueño
 * natural. El panel v1 quedó igual: era el mismo dato dando una vuelta de más.
 */
export function ClientDetailV2Panel({ client }: ClientDetailV2PanelProps) {
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const { data: dossier, isLoading, error, refetch } = useClientV2DetailQuery(client.id)
  const removeClient = useClientV2Remove()
  // v1 pedía ser admin; v2 NOMBRA la capacidad. `has` sigue devolviendo true para
  // un admin, así que el botón no se le mueve a nadie y ahora un rol puede
  // recibirla sin heredar todo lo demás que hace un administrador.
  const { has } = usePermissions()
  const canReadVersions = useCanReadEntityVersions('client')

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <SidePanelSection separated={false}>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-11 w-2/3" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </SidePanelSection>
      ) : !dossier ? (
        /*
          Sin expediente y sin carga en curso, la consulta FALLÓ. Antes esta rama no existía y
          caía en el mismo skeleton de arriba, así que un error se veía idéntico a "cargando" y
          el panel giraba para siempre: la única forma de saber qué había pasado era abrir los
          logs de la API.
        */
        <SidePanelSection separated={false}>
          <QueryErrorState error={error} resource="el expediente" onRetry={() => void refetch()} />
        </SidePanelSection>
      ) : (
        <>
          <SidePanelSection>
            <SidePanelSectionBody className="gap-4">
              <ClientDetailSummary
                name={client.name}
                photo={client.photo}
                detail={{
                  _id: dossier.id,
                  // El dossier v2 se direcciona por el `cli_`, así que su `id` YA es el publicId:
                  // no hay traducción que hacer, sólo decirlo.
                  publicId: dossier.id,
                  code: dossier.code,
                  typePerson: dossier.typePerson,
                  company: dossier.company?.id ?? null,
                  agency: dossier.agency?.id ?? null,
                  // La autoría v2 es `{ id: usr_, fullname }`; el resumen espera
                  // la forma legacy `{ _id, fullname }`. Se adapta acá, en el
                  // borde, para no arrastrar el contrato v2 dentro de un
                  // componente que también sirve al panel v1.
                  advisor: dossier.advisor
                    ? { _id: dossier.advisor.id, fullname: dossier.advisor.fullname }
                    : null,
                  createdBy: dossier.createdBy
                    ? { _id: dossier.createdBy.id, fullname: dossier.createdBy.fullname }
                    : null,
                  updatedBy: dossier.updatedBy
                    ? { _id: dossier.updatedBy.id, fullname: dossier.updatedBy.fullname }
                    : null,
                  createdAt: dossier.createdAt,
                  updatedAt: dossier.updatedAt,
                  name: client.name,
                  email: client.emails[0] ?? null
                }}
                companyName={dossier.company?.name ?? null}
                agencyName={dossier.agency?.name ?? null}
              />
            </SidePanelSectionBody>
          </SidePanelSection>

          <SidePanelSection>
            <ClientDetailActions
              clientId={client.id}
              typePerson={client.typePerson}
              code={client.code}
              name={client.name}
              revalidationStatus={client.revalidation.status}
              hasActiveFlow={client.activeFlow !== null}
              onDelete={removeClient}
              canDelete={has(ClientsPermission.DeleteClient)}
              onNavigate={closePanel}
            />
          </SidePanelSection>

          <Tabs defaultValue="negotiations" className="w-full overflow-hidden">
            <SidePanelSection compact>
              <ScrollFade className="w-full" scrollClassName="overflow-y-hidden pb-1">
                <TabsList className="flex h-auto w-max min-w-full gap-1.5 rounded-none bg-transparent p-0 text-muted-foreground">
                  <TabsTrigger
                    value="negotiations"
                    className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    <Handshake className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-normal break-words">Negociaciones</span>
                  </TabsTrigger>
                  {/* Pestaña del PORTAL: detrás de su flag, igual que sus columnas y su hub. */}
                  {FEATURES.clientPortal ? (
                    <TabsTrigger
                      value="flows"
                      className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                    >
                      <GitBranch className="h-3.5 w-3.5 shrink-0" />
                      <span className="whitespace-normal break-words">Flujos</span>
                    </TabsTrigger>
                  ) : null}
                  <TabsTrigger
                    value="history"
                    className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    <History className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-normal break-words">Historial</span>
                  </TabsTrigger>
                  {/*
                    "Versiones" NO es lo mismo que "Historial": aquélla es la bitácora —quién
                    hizo qué— y ésta es qué DECÍA el expediente después de cada edición. Se
                    ofrece sólo con `ReadEntityVersions`, que ningún rol tiene por defecto
                    (ADR 0029).
                  */}
                  {/* Pestaña de la APP MÓVIL: siempre disponible. Lo que espera a la publicación
                      de la app son sus acciones hacia el cliente (`clientAppLaunched`). */}
                  <TabsTrigger
                    value="app"
                    className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    <Smartphone className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-normal break-words">App</span>
                  </TabsTrigger>
                  {canReadVersions ? (
                    <TabsTrigger
                      value="versions"
                      className="min-h-9 min-w-[7.25rem] flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs leading-tight whitespace-normal break-words data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                    >
                      <FileClock className="h-3.5 w-3.5 shrink-0" />
                      <span className="whitespace-normal break-words">Versiones</span>
                    </TabsTrigger>
                  ) : null}
                </TabsList>
              </ScrollFade>
            </SidePanelSection>

            <SidePanelSection separated={false}>
              <TabsContent value="negotiations" className="mt-0">
                <ClientNegotiationsTab clientId={client.id} />
              </TabsContent>
              {FEATURES.clientPortal ? (
                <TabsContent value="flows" className="mt-0">
                  <ClientFlowsTab
                    clientId={client.id}
                    clientType={client.typePerson}
                    clientName={client.name}
                    // Jurídica soporta varios correos; el enlace se envía a UNO, así
                    // que se toma el primario. En v2 `emails` es siempre una lista.
                    clientEmail={client.emails[0] ?? null}
                    hasActiveFlow={client.activeFlow !== null}
                  />
                </TabsContent>
              ) : null}
              <TabsContent value="app" className="mt-0">
                <ClientAppTab
                  clientId={client.id}
                  clientName={client.name}
                  // Jurídica soporta varios correos; la cuenta es UNA, así que se
                  // propone el primario. Se propone, no se impone: el diálogo lo deja
                  // cambiar antes de abrir la cuenta.
                  clientEmail={client.emails[0] ?? null}
                />
              </TabsContent>
              <TabsContent value="history" className="mt-0">
                <ClientHistoryTab clientId={client.id} />
              </TabsContent>
              {canReadVersions ? (
                <TabsContent value="versions" className="mt-0">
                  <VersionTimeline kind="client" entityId={client.id} />
                </TabsContent>
              ) : null}
            </SidePanelSection>
          </Tabs>
        </>
      )}
    </div>
  )
}
